import type { ModuleID, VersionString, ViewID } from "@bestowinc/tinkertoy-core";

/** Bounded New Relic TinkertoyMetric `metricName` values for Tinkertoy V2 performance telemetry. */
export const PERFORMANCE_METRIC_NAMES = {
  shellBootstrap: "tinkertoy.shell_bootstrap",
  loadNavigation: "tinkertoy.load_navigation",
  viewBundleLoad: "tinkertoy.view_bundle_load",
  moduleBundleLoad: "tinkertoy.module_bundle_load",
  actionLatency: "tinkertoy.action_latency",
  revalidation: "tinkertoy.revalidation",
  v2Readiness: "tinkertoy.v2_readiness",
} as const;

export type PerformanceMetricName =
  (typeof PERFORMANCE_METRIC_NAMES)[keyof typeof PERFORMANCE_METRIC_NAMES];

export type PerformanceOutcome =
  | "success"
  | "failure"
  | "cancelled"
  | "superseded";

/**
 * How the bundle load was initiated, when observable at the instrumentation
 * seam. ES-module runtime cache hits without a shell dedupe entry report as
 * `cold` (still observable as a loadModule invocation).
 */
export type BundleCacheMode =
  | "cold"
  | "in_flight"
  | "cached"
  | "preload"
  | "dev_override";

export type PerformanceMetricAttributes = {
  environment: string;
  shellVersion: string;
  outcome: PerformanceOutcome;
  /** Duration in milliseconds. Also emitted as the TinkertoyMetric `value`. */
  durationMs: number;
  viewId?: ViewID;
  viewVersion?: VersionString;
  moduleId?: ModuleID;
  moduleVersion?: VersionString;
  flowId?: string;
  cacheMode?: BundleCacheMode;
  /** `module` or `view_interaction` for action latency metrics. */
  actionScope?: "module" | "view_interaction";
  actionName?: string;
  /** Stable error code when outcome is failure (e.g. NETWORK_ERROR, HTTP_500). */
  errorCode?: string;
  /** True when this metric was emitted despite sampling (always true for failures). */
  sampled?: boolean;
};

export type ActionScope = "module" | "view_interaction";
